import {EduTreeCourse, EduTreeLesson, EduTreeSection} from "protocol/edutrading/GetEducationalContentTree";
import {ThemeType} from "platform/enum/ThemeType";
import Utils from "platform/util/Utils";
import {EduCourseInfo, EduTradingPackage, PlaylistInfo} from "core/redux/eduTrading/EduTradingReduxState";
import Platform from "platform/Platform";
import {StorageKey} from "enum/StorageKey";
import {StoreState} from "core/redux/StoreState";
import {DoRegisterUserActivity} from "core/redux/account/AccountReduxActions";
import {UserActivityType} from "enum/UserActivityType";
import {EduTradingReportsUtil} from "core/util/EduTradingReportsUtil";
import {Preference} from "core/util/Preference";
import {InsertClientPreference, SetClientPreference} from "core/redux/settings/SettingsReduxActions";
import {EduTradingCompleteLesson} from "core/redux/eduTrading/EduTradingReduxActions";
import {TSMap} from "typescript-map";
import {EduTradingLessonsCountType} from "enum/EduTradingLessonsCountType";

const PackageEmoji: {[key: string]: string} = {};
PackageEmoji["Elite"] = "🚀";
PackageEmoji["Expert"] = "✨";

export class EduTradingPlaylistUtil {

    private static CourseColorsLight: string[] = ["#F1F3DC", "#E7F1F6", "#EBEBEF", "#FFEAC7", "#EBEBEF", "#FFEAC7", "#EBEBEF"];
    private static CourseColorsDark: string[] = ["#3F4B50", "#4D4D58", "#484135", "#4D4D58", "#4B4C43", "#484135", "#4D4D58"];

    public static getCourseColor = (index: number, theme: ThemeType): string => {
        const colors: string[] = theme === ThemeType.Dark ? this.CourseColorsDark : this.CourseColorsLight;
        return colors[index <= colors.length ? index : Utils.randomInt(0, colors.length - 1)];
    }

    public static getPackageEmoji = (packageName: string): string => {
        return Utils.nullToEmpty(PackageEmoji[packageName]);
    }

    public static getAllLessons = (course: EduTreeCourse): EduTreeLesson[] => {
        const lessons: EduTreeLesson[] = [];
        course?.Sections?.forEach((section: EduTreeSection) => {
            if (section?.Lessons) {
                section.Lessons.forEach((lesson: EduTreeLesson) => lessons.push(lesson));
            }
        });
        return lessons;
    }

    public static IsAllLocked = (course: EduTreeCourse, activePackage: EduTradingPackage, packages: EduTradingPackage[]): boolean => {
        if (course?.Sections) {
            for (let i: number = 0; i < course.Sections.length; i++) {
                const section: EduTreeSection = course.Sections[i];
                if (section?.Lessons) {
                    for (let j: number = 0; j < section.Lessons.length; j++) {
                        if (!this.IsLocked(section.Lessons[j], activePackage, packages)) {
                            return false;
                        }
                    }
                }
            }
        }
        return true;
    }

    public static IsLocked = (lesson: EduTreeLesson, activePackage: EduTradingPackage, packages: EduTradingPackage[]): boolean => {
        let locked: boolean = true;
        if (activePackage && Utils.isArrayNotEmpty(packages)) {
            const usePackage: EduTradingPackage = activePackage.isEnabled ? activePackage : packages.filter((p: EduTradingPackage) => p.name === "Default")[0];
            for (let i: number = 0; i < packages.length; i++) {
                const eduPackage: EduTradingPackage = packages[i];
                if (eduPackage.name === lesson.LeastRequiredPackageName) {
                    locked = eduPackage.id > usePackage?.id
                    break;
                }
            }
        }
        return locked;
    }

    public static GetLessonToPlay = (
        course: EduTreeCourse,
        allLessons: EduTreeLesson[],
        courseInfo: EduCourseInfo,
        courseProgress: PlaylistInfo,
        packages: EduTradingPackage[]
    ): EduTreeLesson => {
        if (Utils.isArrayNotEmpty(courseProgress?.watchedLessons)) {
            const {eduTrading} = Platform.reduxState<StoreState>();
            const activePackage: EduTradingPackage = eduTrading?.ActivePackage;

            let unwatchedLesson: EduTreeLesson;
            let firstWatchedLesson: EduTreeLesson;
            for (let i: number = 0; i < allLessons.length; i++) {
                const lesson: EduTreeLesson = allLessons[i];
                const IsWatched: boolean = courseProgress.watchedLessons?.includes(lesson.LessonGuid);
                if (IsWatched && Utils.isNull(firstWatchedLesson)) {
                    firstWatchedLesson = lesson;
                }
                if (!IsWatched && !this.IsLocked(lesson, activePackage, packages)) {
                    unwatchedLesson = lesson;
                    break;
                }
            }
            return unwatchedLesson || firstWatchedLesson;
        } else {
            if (Utils.isNull(courseInfo)) {
                Platform.dispatch(DoRegisterUserActivity({
                    userActivity: UserActivityType.EduCourseStarted,
                    comment: EduTradingReportsUtil.FormatCourseInfo(course),
                    specificData: course?.Id
                }));
                setTimeout(() => this.SaveCourseInfo(course, {
                    started: true
                }), 0);
            }
            return course.Sections?.[0]?.Lessons?.[0];
        }
    }

    private static SaveCourseInfo = (course: EduTreeCourse, courseInfo: EduCourseInfo): void => {
        if (course) {
            const preference: Preference<{[key: number]: EduCourseInfo}> = Platform.reduxState<StoreState>().settings.preferences.get(StorageKey.AcademyCoursesInfo);
            const coursesInfo: {[key: number]: EduCourseInfo} = Utils.deepCopy(preference?.value || {});
            coursesInfo[course.Id] = courseInfo;
            const newPreference: Preference<{[key: number]: EduCourseInfo}> = Preference.Of(coursesInfo);
            Platform.dispatch(SetClientPreference({
                key: StorageKey.AcademyCoursesInfo,
                preference: newPreference
            }));
            Platform.dispatch(InsertClientPreference({
                key: StorageKey.AcademyCoursesInfo,
                preference: newPreference
            }));
        }
    }

    public static GetCourseProgress = (guids: string[], allLessons: EduTreeLesson[]): number => {
        if (guids && allLessons) {
            return Math.min(Math.round(guids.length /  allLessons.length * 100), 100);
        }
        return 0;
    }

    public static SetWatchedLesson = (
        course: EduTreeCourse,
        courseProgress: PlaylistInfo,
        allLessons: EduTreeLesson[],
        lesson: EduTreeLesson,
        activePackage: EduTradingPackage,
        packages: EduTradingPackage[]
    ): {
        nextLesson: EduTreeLesson;
        progress: number;
    } => {
        let nextLesson: EduTreeLesson;
        let progress: number = courseProgress.progress;
        let guids: string[] = courseProgress.watchedLessons;
        if (!lesson.IsCompleted) {
            Platform.dispatch(EduTradingCompleteLesson({lessonGuid: lesson.LessonGuid, courseId: course.Id}));
            guids = [...courseProgress.watchedLessons, lesson.LessonGuid];
            progress = this.GetCourseProgress(guids, allLessons);
        }

        let watchedLessonIndex: number;
            for (let i: number = 0; i < allLessons.length; i++) {
                const curLesson: EduTreeLesson = allLessons[i];
                if (curLesson.LessonGuid === lesson.LessonGuid) {
                    watchedLessonIndex = i;
                }
                if (i > watchedLessonIndex && !this.IsLocked(curLesson, activePackage, packages)) {
                    nextLesson = curLesson;
                    break;
                }
            }

        return {
            nextLesson,
            progress
        };
    }

    public static GetCoursesProgress = (courses: EduTreeCourse[]) => {
        const coursesProgress: TSMap<number, PlaylistInfo> = new TSMap<number, PlaylistInfo>();
        courses?.forEach((course: EduTreeCourse) => {
            let watchedLessons: string[] = [];
            let allLessons: number = 0;
            let progress: number = 0;
            course?.Sections?.forEach((section: EduTreeSection) => {
                watchedLessons = [...watchedLessons, ...section.Lessons.filter((lesson: EduTreeLesson) => lesson.IsCompleted).map(item => item.LessonGuid)];
                allLessons += section.Lessons.length;
            })
            if (Utils.isArrayNotEmpty(watchedLessons)) {
                progress = Math.round(watchedLessons.length /  allLessons * 100)
            }
            coursesProgress.set(course.Id, {
                watchedLessons,
                progress
            })
        });
        return coursesProgress;
    }

    public static CalculateLessonsCount = (courses: EduTreeCourse[]): {[key: string]: number} => {
        return courses?.reduce((acc: {[key: string]: number}, item: EduTreeCourse) => {
            let lessonCount: number = 0;
            let completedCount: number = 0;
            item?.Sections?.forEach((section: EduTreeSection) => {
                lessonCount += section.Lessons.length;
                completedCount += section.Lessons?.filter((lesson: EduTreeLesson) => lesson.IsCompleted)?.length;
            });
            return {
                [EduTradingLessonsCountType.All]: (acc[EduTradingLessonsCountType.All] ?? 0) + lessonCount,
                [EduTradingLessonsCountType.Completed]: (acc[EduTradingLessonsCountType.Completed] ?? 0) + completedCount
            }
        }, {});
    }

    public static SetVideoStarted = (startedLessons: string[], lessonId: string): string[] => {
        const copy = [...startedLessons];
        if (!startedLessons.includes(lessonId)) {
            copy.push(lessonId)
        }
        return copy;
    }
}
